import { createRoute } from "@tanstack/react-router";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import { Card, CardBody } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useMyCards } from "../../hooks/useMyCards";
import type { MyCard } from "../../schemas/myCards";

/**
 * Holder „Moje karty" — seznam tankovacích karet navázaných na přihlášeného
 * uživatele. Číslo karty maskované (last-6), zdroj = `/api/tenant/my-cards`.
 *
 * Princip #6: backend RLS scope; frontend nikdy nepředpokládá tenant scope.
 */
export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/my-cards",
  component: MyCardsPage,
});

const STATUS_LABEL: Record<string, string> = {
  active: "Aktivní",
  blocked: "Blokovaná",
  archived: "Archivovaná",
};

const STATUS_COLOR: Record<string, BadgeColors> = {
  active: "success",
  blocked: "error",
  archived: "gray",
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

function MyCardsPage() {
  const query = useMyCards();
  const cards = query.data?.cards ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="tenant-my-cards">
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-2xl font-bold text-primary">Moje karty</h1>
        <p className="m-0 text-tertiary">
          Tankovací karty vedené na vás. Z bezpečnostních důvodů zobrazujeme jen
          posledních šest míst čísla karty.
        </p>
      </header>

      {query.isLoading && (
        <div data-testid="my-cards-loading">
          <LoadingIndicator />
        </div>
      )}

      {query.isError && (
        <InlineAlert variant="warning" title="Načítání karet selhalo">
          <p data-testid="my-cards-error">Zkuste obnovit stránku.</p>
        </InlineAlert>
      )}

      {query.data && cards.length === 0 && (
        <InlineAlert variant="info" title="Zatím nemáte přiřazené žádné karty">
          Jakmile vám správce přiřadí tankovací kartu, objeví se tady.
        </InlineAlert>
      )}

      {cards.length > 0 && (
        <ul className="grid gap-3 list-none p-0 m-0" data-testid="my-cards-list">
          {cards.map((card) => (
            <li key={card.fuel_card_id}>
              <MyCardItem card={card} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MyCardItem({ card }: { card: MyCard }) {
  return (
    <Card data-testid={`my-card-${card.fuel_card_id}`}>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary m-0 truncate">
              {card.card_label ?? "Tankovací karta"}
            </p>
            <p
              className="text-base font-mono tracking-wider text-tertiary m-0 mt-1"
              data-testid="my-card-number"
            >
              {card.card_number_display ?? "—"}
            </p>
          </div>
          {/* Native Badge nepropaguje data-testid → nese ho span wrapper. */}
          <span className="shrink-0" data-testid="my-card-status">
            <Badge color={STATUS_COLOR[card.status] ?? "gray"}>
              {statusLabel(card.status)}
            </Badge>
          </span>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-tertiary m-0">
          <dt className="m-0">Držitel</dt>
          <dd className="m-0 text-right text-primary truncate">{card.holder_label}</dd>
          {card.network && (
            <>
              <dt className="m-0">Síť</dt>
              <dd className="m-0 text-right text-primary">{card.network}</dd>
            </>
          )}
          {card.expiry_date && (
            <>
              <dt className="m-0">Platnost do</dt>
              <dd className="m-0 text-right text-primary">{card.expiry_date}</dd>
            </>
          )}
        </dl>
      </CardBody>
    </Card>
  );
}
